'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';
import Link from 'next/link';

export default function SignIn() {
  const router = useRouter();
  const { signIn, signInWithGoogle } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  const handleGoogle = async () => {
    setError('');
    setGoogleLoading(true);
    const { error: googleError } = await signInWithGoogle();
    if (googleError) {
      setError(googleError.message || 'Could not sign in with Google. Please try again.');
      setGoogleLoading(false);
    }
    // On success the browser redirects to Google, so no further action here.
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const { data, error: signInError } = await signIn(email, password);

      if (signInError) {
        const msg = (signInError.message || '').toLowerCase();
        if (msg.includes('email not confirmed') || msg.includes('not confirmed') || msg.includes('confirm your email')) {
          setError('Please confirm your email first. Check your inbox for the confirmation link we sent when you signed up.');
        } else if (msg.includes('invalid login') || msg.includes('invalid credentials')) {
          setError('Incorrect email or password. Please try again.');
        } else {
          setError(signInError.message || 'Failed to sign in');
        }
        setLoading(false);
        return;
      }

      if (data?.user) {
        try {
          const response = await fetch('/api/user-role', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ userId: data.user.id }),
          });

          const { role, error: roleError } = await response.json();

          if (roleError) {
            console.error('Error fetching user role:', roleError);
            router.push('/client-dashboard');
            return;
          }

          const userRole = role || 'client';

          if (userRole === 'admin') {
            router.push('/admin-control-panel');
          } else if (userRole === 'editor') {
            router.push('/editor-workspace');
          } else {
            router.push('/client-dashboard');
          }
        } catch (fetchError) {
          console.error('Error fetching role:', fetchError);
          router.push('/client-dashboard');
        }
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
      setLoading(false);
    }
  };

  const features = [
    {
      title: 'Secure & Reliable',
      desc: 'Your data is protected with enterprise-grade security',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      ),
    },
    {
      title: 'Fast Turnaround',
      desc: 'Get your edited images delivered on time, every time',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      ),
    },
    {
      title: 'Expert Editors',
      desc: 'Professional editors ensuring premium quality results',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87m6-1.13a4 4 0 10-4-4 4 4 0 004 4zm6 0a4 4 0 10-3-6.87" />
        </svg>
      ),
    },
    {
      title: 'Easy Downloads',
      desc: 'Download your images in high resolution with ease',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 12v6m0 0l-2-2m2 2l2-2" />
        </svg>
      ),
    },
  ];

  const Logo = ({ size = 'w-10 h-10', text = 'text-2xl' }: { size?: string; text?: string }) => (
    <Link href="/homepage" className="flex items-center space-x-2.5 group">
      <svg className={`${size} transition-all duration-300 group-hover:scale-110`} viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M16 1 L27 8 L27 17 L16 24 L5 17 L5 8 Z" fill="url(#logoGrad1)" />
        <path d="M16 24 L27 17 L27 24 L16 31 L5 24 L5 17 Z" fill="url(#logoGrad2)" />
        <defs>
          <linearGradient id="logoGrad1" x1="5" y1="1" x2="27" y2="24" gradientUnits="userSpaceOnUse">
            <stop stopColor="#A78BFA" />
            <stop offset="1" stopColor="#7C3AED" />
          </linearGradient>
          <linearGradient id="logoGrad2" x1="5" y1="17" x2="27" y2="31" gradientUnits="userSpaceOnUse">
            <stop stopColor="#7C3AED" />
            <stop offset="1" stopColor="#5B21B6" />
          </linearGradient>
        </defs>
      </svg>
      <span className={`${text} font-bold tracking-tight text-white`}>shashio</span>
    </Link>
  );

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden bg-[#0B0D17] text-white">
      {/* ===== HeroV3 background: breathing glows + dot-grid texture ===== */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 left-1/3 w-[1200px] h-[1200px] bg-purple-600/10 rounded-full blur-[160px] animate-glow-breathe" />
        <div className="absolute bottom-1/4 right-1/3 w-[900px] h-[900px] bg-blue-600/5 rounded-full blur-[140px] animate-glow-breathe-delayed" />
        <div
          className="absolute inset-0 opacity-[0.025]"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />
      </div>

      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 lg:px-12 py-10">
        <div className="grid lg:grid-cols-[1fr_460px] gap-10 xl:gap-16 items-center">
          {/* ===================== LEFT COLUMN ===================== */}
          <div className="hidden lg:block space-y-8">
            <Logo />

            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/[0.04] border border-white/[0.12] backdrop-blur-sm text-purple-200 text-xs font-medium">
              <span className="text-sm">✨</span>
              <span>Professional Real Estate Photo Editing</span>
            </div>

            {/* Headline */}
            <h1 className="text-5xl xl:text-6xl font-bold leading-[1.1] tracking-tight">
              <span className="text-white">Welcome Back!</span>
              <br />
              <span className="text-white">Let&apos;s continue creating</span>
              <br />
              <span className="bg-gradient-to-r from-purple-400 via-purple-300 to-blue-400 bg-clip-text text-transparent">
                excellence.
              </span>
            </h1>

            <p className="text-lg text-gray-400 leading-relaxed max-w-xl">
              Sign in to your account to manage your orders, track progress, and download your professionally edited images.
            </p>

            {/* Feature List */}
            <div className="grid sm:grid-cols-2 gap-4 max-w-xl pt-2">
              {features.map((feature, idx) => (
                <div
                  key={idx}
                  className="group flex items-start gap-3.5 bg-white/[0.03] hover:bg-white/[0.06] backdrop-blur-sm rounded-xl p-4 border border-white/[0.08] hover:border-purple-500/30 transition-all duration-300"
                >
                  <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500/20 to-blue-500/10 border border-purple-500/20 flex items-center justify-center text-purple-300 group-hover:scale-110 transition-transform">
                    {feature.icon}
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-white font-semibold text-[14px] leading-tight">{feature.title}</h3>
                    <p className="text-xs text-gray-400 leading-relaxed mt-1">{feature.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Trust Row */}
            <div className="flex items-center gap-4 pt-2">
              <div className="flex -space-x-2">
                {[1, 2, 3, 4].map((i) => (
                  <div
                    key={i}
                    className="w-9 h-9 rounded-full bg-gradient-to-br from-purple-400 to-blue-400 border-2 border-[#0B0D17] flex items-center justify-center text-white text-xs font-semibold"
                  >
                    {String.fromCharCode(64 + i)}
                  </div>
                ))}
              </div>
              <div className="flex flex-col">
                <div className="flex items-center gap-0.5">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <svg key={star} className="w-4 h-4 text-purple-400" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
                <p className="text-sm text-gray-400 mt-1">Trusted by 1,000+ Real Estate Professionals</p>
              </div>
            </div>
          </div>

          {/* ===================== RIGHT COLUMN - SIGN IN CARD ===================== */}
          <div className="relative w-full max-w-[460px] mx-auto animate-float-slow">
            {/* Mobile Logo */}
            <div className="lg:hidden flex justify-center mb-8">
              <Logo size="w-9 h-9" text="text-xl" />
            </div>

            {/* Purple glow behind card */}
            <div className="absolute -inset-4 bg-purple-600/15 rounded-3xl blur-[80px] -z-10" />

            <div className="relative bg-[#13151F]/95 backdrop-blur-2xl rounded-2xl border-2 border-white/[0.12] shadow-[0_30px_80px_rgba(0,0,0,0.6),0_10px_30px_rgba(124,58,237,0.15)] overflow-hidden transition-all duration-500 hover:border-purple-500/30 hover:shadow-[0_35px_90px_rgba(124,58,237,0.4)]">
              {/* App window title bar */}
              <div className="flex items-center justify-between px-4 py-2.5 bg-gradient-to-r from-white/[0.08] to-white/[0.04] border-b border-white/[0.08]">
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-red-500/80" />
                  <div className="w-3 h-3 rounded-full bg-yellow-500/80" />
                  <div className="w-3 h-3 rounded-full bg-green-500/80" />
                </div>
                <div className="text-xs text-gray-400 font-medium">Secure Sign In</div>
                <div className="w-12" />
              </div>

              <div className="p-8 sm:p-9">
                {/* Heading */}
                <div className="mb-7">
                  <h2 className="text-[26px] font-bold leading-tight">
                    Sign in to your{' '}
                    <span className="bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">
                      account
                    </span>
                  </h2>
                  <p className="text-gray-400 mt-2 text-[15px]">
                    Enter your credentials to access your dashboard
                  </p>
                </div>

                {/* Form */}
                <form onSubmit={handleSignIn} className="space-y-5">
                  {/* Email */}
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium mb-2 text-gray-200">
                      Email Address
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                        <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <input
                        type="email"
                        id="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="Enter your email address"
                        required
                        className="w-full pl-12 pr-4 py-3.5 rounded-xl bg-white/[0.03] border border-white/[0.12] text-white placeholder-gray-500 transition-all duration-200 focus:border-purple-500 focus:bg-white/[0.06] focus:outline-none focus:ring-2 focus:ring-purple-500/20"
                      />
                    </div>
                  </div>

                  {/* Password */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label htmlFor="password" className="block text-sm font-medium text-gray-200">
                        Password
                      </label>
                      <Link
                        href="/auth/forgot-password"
                        className="text-sm font-medium text-purple-400 hover:text-purple-300 transition-colors"
                      >
                        Forgot Password?
                      </Link>
                    </div>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                        <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                      </div>
                      <input
                        type={showPassword ? 'text' : 'password'}
                        id="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Enter your password"
                        required
                        className="w-full pl-12 pr-12 py-3.5 rounded-xl bg-white/[0.03] border border-white/[0.12] text-white placeholder-gray-500 transition-all duration-200 focus:border-purple-500 focus:bg-white/[0.06] focus:outline-none focus:ring-2 focus:ring-purple-500/20"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((v) => !v)}
                        className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-500 hover:text-gray-300 transition-colors"
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                      >
                        {showPassword ? (
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                          </svg>
                        ) : (
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Remember / Keep signed in */}
                  <div className="flex items-center justify-between">
                    <label className="flex items-center gap-2.5 cursor-pointer group">
                      <input
                        type="checkbox"
                        checked={rememberMe}
                        onChange={(e) => setRememberMe(e.target.checked)}
                        className="w-4 h-4 rounded border-white/20 bg-white/5 text-purple-600 accent-purple-600 focus:ring-2 focus:ring-purple-500/20 focus:ring-offset-0 transition-all"
                      />
                      <span className="text-sm text-gray-300 group-hover:text-white transition-colors">Remember me</span>
                    </label>
                    <span className="text-sm text-gray-500">Keep me signed in</span>
                  </div>

                  {/* Error */}
                  {error && (
                    <div className="px-4 py-3 rounded-xl text-sm flex items-start gap-3 bg-red-500/10 border border-red-500/20 text-red-400">
                      <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                      <span>{error}</span>
                    </div>
                  )}

                  {/* Submit */}
                  <button
                    type="submit"
                    disabled={loading}
                    className="group w-full mt-1 font-bold py-3.5 rounded-xl transition-all duration-300 flex items-center justify-center gap-2 bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-500 hover:to-purple-400 text-white shadow-xl shadow-purple-500/30 hover:shadow-purple-500/50 hover:-translate-y-0.5 hover:scale-[1.01] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:scale-100"
                  >
                    {loading ? (
                      <>
                        <svg className="w-5 h-5 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        Signing In...
                      </>
                    ) : (
                      <>
                        Sign In
                        <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                        </svg>
                      </>
                    )}
                  </button>
                </form>

                {/* Divider */}
                <div className="flex items-center gap-4 my-6">
                  <div className="flex-1 h-px bg-white/[0.10]" />
                  <span className="text-xs text-gray-500 font-medium">or continue with</span>
                  <div className="flex-1 h-px bg-white/[0.10]" />
                </div>

                {/* Google */}
                <button
                  type="button"
                  onClick={handleGoogle}
                  disabled={googleLoading || loading}
                  className="group w-full font-semibold py-3.5 rounded-xl transition-all duration-300 flex items-center justify-center gap-3 bg-white/[0.04] hover:bg-white/[0.08] border border-white/[0.12] hover:border-white/[0.20] text-white disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {googleLoading ? (
                    <svg className="w-5 h-5 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                    </svg>
                  )}
                  <span>{googleLoading ? 'Redirecting to Google...' : 'Continue with Google'}</span>
                </button>

                {/* Sign Up hint */}
                <div className="mt-7 text-center">
                  <p className="text-sm text-gray-400">
                    New to Shashio? Use{' '}
                    <span className="font-semibold text-purple-400">Continue with Google</span>{' '}
                    above to create your account in seconds.
                  </p>
                </div>
              </div>
            </div>

            {/* Footer */}
            <p className="mt-6 text-center text-sm text-gray-600">
              © 2024 Shashio. All rights reserved.
            </p>
          </div>
        </div>
      </div>

      {/* ===== Animations (from HeroV3) ===== */}
      <style jsx global>{`
        @keyframes float-slow {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
        @keyframes glow-breathe {
          0%, 100% { opacity: 0.1; transform: scale(1); }
          50% { opacity: 0.15; transform: scale(1.05); }
        }
        @keyframes glow-breathe-delayed {
          0%, 100% { opacity: 0.05; transform: scale(1); }
          50% { opacity: 0.08; transform: scale(1.03); }
        }
        .animate-float-slow { animation: float-slow 8s ease-in-out infinite; }
        .animate-glow-breathe { animation: glow-breathe 8s ease-in-out infinite; }
        .animate-glow-breathe-delayed { animation: glow-breathe-delayed 10s ease-in-out infinite; animation-delay: 2s; }
      `}</style>
    </section>
  );
}
